// TraderTekno Referral Service — thin API client for backend /api/referral/*
// Backend business logic: 30% commission on first payment, 10% on recurring.
// Min withdrawal Rp100k, manual bank transfer by admin.

import { getAPIUrl } from '../../config/api.config';
import { getAuthTokenAsync, notifySessionExpired } from '../api/authTokenManager';

// ---------------------------------------------------------------------------
// Types matching backend response shapes
// ---------------------------------------------------------------------------

export interface ReferralCodeDTO {
  code: string;
  shareUrl: string;
  totalClicks: number;
  totalConversions: number;
}

export interface ReferralStats {
  code: string;
  shareUrl: string;
  totalClicks: number;
  totalConversions: number;
  totalReferrals: number;
  totalEarnings: number;
  availableBalance: number;
  pendingWithdrawal: number;
  paidOut: number;
}

export interface ReferralEarning {
  id: string;
  commission_amount: number;
  is_first_payment: number;
  payment_order_id: string;
  referee_user_id: string;
  status: 'pending' | 'locked' | 'paid' | 'void';
  created_at: string;
}

export interface ReferralEarningsPage {
  items: ReferralEarning[];
  total: number;
  limit: number;
  offset: number;
}

export interface ReferralWithdrawal {
  id: string;
  amount: number;
  bank_code: string;
  account_number: string;
  account_holder: string;
  status: 'requested' | 'paid' | 'rejected';
  transfer_reference?: string;
  rejection_reason?: string;
  requested_at: string;
  processed_at?: string;
}

export interface VisitorData {
  deviceFingerprint?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  referrerUrl?: string;
}

export type TrackResult =
  | { accepted: true; attributionId: string }
  | { accepted: false; reason: 'invalid_code' | 'rate_limited' };

export type AttributeResult =
  | { accepted: true; referralId: string }
  | { accepted: false; reason: 'invalid_code' | 'self_referral' | 'already_attributed' | 'no_active_attribution' };

export type WithdrawResult =
  | { accepted: true; withdrawalId: string; lockedAmount: number }
  | { accepted: false; reason: 'below_minimum' | 'insufficient_balance' | 'invalid_amount' };

// ---------------------------------------------------------------------------
// Internal fetch helpers
// ---------------------------------------------------------------------------

async function jsonFetch(path: string, init: RequestInit & { auth?: string } = {}): Promise<Response> {
  const base = getAPIUrl();
  const headers = new Headers(init.headers);
  if (init.auth) headers.set('Authorization', `Bearer ${init.auth}`);
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  return fetch(`${base}${path}`, { ...init, headers });
}

async function authed<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getAuthTokenAsync();
  if (!token) throw new Error('unauthenticated');
  const res = await jsonFetch(path, { ...init, auth: token });
  if (res.status === 401) {
    notifySessionExpired();
    throw new Error('Sesi berakhir, silakan login kembali');
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${init.method ?? 'GET'} ${path} failed: ${res.status} ${text}`);
  }
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// API client
// ---------------------------------------------------------------------------

export class ReferralService {
  private static instance: ReferralService;

  private constructor() {}

  static getInstance(): ReferralService {
    if (!ReferralService.instance) {
      ReferralService.instance = new ReferralService();
    }
    return ReferralService.instance;
  }

  /** Public — no auth required */
  async trackVisit(code: string, visitor: VisitorData): Promise<TrackResult> {
    const res = await jsonFetch('/api/referral/track', {
      method: 'POST',
      body: JSON.stringify({ code, ...visitor }),
    });
    return res.json() as Promise<TrackResult>;
  }

  async getMyCode(): Promise<ReferralCodeDTO> {
    return authed<ReferralCodeDTO>('/api/referral/code');
  }

  async attributeSignup(input: {
    code: string;
    deviceFingerprint?: string;
    channel?: string;
  }): Promise<AttributeResult> {
    return authed<AttributeResult>('/api/referral/attribute', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async getStats(): Promise<ReferralStats> {
    return authed<ReferralStats>('/api/referral/stats');
  }

  async getEarnings(limit = 50, offset = 0): Promise<ReferralEarningsPage> {
    return authed<ReferralEarningsPage>(`/api/referral/earnings?limit=${limit}&offset=${offset}`);
  }

  async requestWithdrawal(input: {
    amount: number;
    bankCode: string;
    accountNumber: string;
    accountHolder: string;
  }): Promise<WithdrawResult> {
    return authed<WithdrawResult>('/api/referral/withdraw', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async listMyWithdrawals(): Promise<{ items: ReferralWithdrawal[] }> {
    return authed<{ items: ReferralWithdrawal[] }>('/api/referral/withdrawals');
  }
}
